/**
 * TanStack Query hooks for `affiliate-notes`. Thin wrappers over ./api,
 * scoped per affiliate_id. List caches are keyed by affiliate so switching
 * between affiliates does not thrash a single cache slot.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	affiliateNotesQueryKeys,
	createNote,
	createTag,
	deleteNote,
	deleteTag,
	listNotes,
	listTags,
	type AffiliateNoteRow,
	type AffiliateTagRow,
	type CreateNoteBody,
	type CreateTagBody,
	type ListNotesResponse,
	type ListTagsResponse,
} from "./api";

// ---------------------------------------------------------------------------
// notes
// ---------------------------------------------------------------------------

export function useAffiliateNotesList(
	affiliateId: string,
): UseQueryResult<ListNotesResponse, Error> {
	return useQuery({
		queryKey: affiliateNotesQueryKeys.notes(affiliateId),
		queryFn: () => listNotes(affiliateId),
		enabled: affiliateId.length > 0,
	});
}

export function useAffiliateNoteCreate(): UseMutationResult<
	AffiliateNoteRow,
	Error,
	CreateNoteBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createNote,
		onSuccess: (_row, vars) => {
			qc.invalidateQueries({
				queryKey: affiliateNotesQueryKeys.notes(vars.affiliate_id),
			});
		},
	});
}

export function useAffiliateNoteDelete(
	affiliateId: string,
): UseMutationResult<{ id: string; deleted: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteNote,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: affiliateNotesQueryKeys.notes(affiliateId),
			});
		},
	});
}

// ---------------------------------------------------------------------------
// tags
// ---------------------------------------------------------------------------

export function useAffiliateTagsList(
	affiliateId: string,
): UseQueryResult<ListTagsResponse, Error> {
	return useQuery({
		queryKey: affiliateNotesQueryKeys.tags(affiliateId),
		queryFn: () => listTags(affiliateId),
		enabled: affiliateId.length > 0,
	});
}

export function useAffiliateTagCreate(): UseMutationResult<
	AffiliateTagRow,
	Error,
	CreateTagBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createTag,
		onSuccess: (_row, vars) => {
			qc.invalidateQueries({
				queryKey: affiliateNotesQueryKeys.tags(vars.affiliate_id),
			});
		},
	});
}

export function useAffiliateTagDelete(
	affiliateId: string,
): UseMutationResult<{ id: string; deleted: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteTag,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: affiliateNotesQueryKeys.tags(affiliateId),
			});
		},
	});
}
